/**
 * StrategyLineageChip — Phase V (V4) fork lineage indicator.
 *
 * Shown on strategies whose inputManifest — or, since live-2 fix wave 7, whose
 * durable head field `forkLineage` — carries predecessorStrategyId (D28).
 * Popover exposes the hypothesis and the inputDiff summary so experiment chains
 * stay readable.
 */
import React, { useState } from 'react';
import { Box, Chip, Popover, Typography } from '@mui/material';
import { CallSplit as ForkIcon } from '@mui/icons-material';
import type { StrategyForkLineage, StrategyInputManifest } from '@modules/shared-types';

/**
 * Live-2 fix wave 7 (W2, F-LIVE2-25): the chip also reads the head's DURABLE
 * `forkLineage`, which carries no `inputDiff` — so it takes the lineage fields it
 * renders, not the whole manifest. Fix round 1 (M1): the two record shapes it accepts
 * carry fields it never renders, so both are admitted by name — a whole
 * `StrategyInputManifest` and a whole `StrategyForkLineage` are valid arguments, typed
 * literals included.
 */
export type StrategyLineageChipManifest = Pick<
  StrategyInputManifest,
  'predecessorStrategyId' | 'predecessorVersion' | 'forkHypothesis' | 'inputDiff'
>
& Partial<StrategyInputManifest>
& Partial<Pick<StrategyForkLineage, 'forkedAt' | 'taskId'>>;

export interface StrategyLineageChipProps {
  manifest: StrategyLineageChipManifest;
  predecessorName?: string;
  /**
   * Live-2 fix wave 7 (W2): the fork's generation failed before a single block was
   * saved. The lineage is kept (owner decision D-L2-4, no deletion), so the chip says
   * so instead of leaving an empty strategy that merely claims a predecessor.
   */
  generationFailed?: boolean;
}

const shortId = (id?: string): string => (id ? `${id.slice(0, 6)}…` : '?');

export const StrategyLineageChip: React.FC<StrategyLineageChipProps> = ({ manifest, predecessorName, generationFailed }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!manifest.predecessorStrategyId) return null;
  const source = predecessorName
    ? `Forked from “${predecessorName}”`
    : `Forked from ${shortId(manifest.predecessorStrategyId)}`;
  const label = generationFailed ? `${source} · generation failed` : source;

  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        {...(generationFailed ? { color: 'warning' as const } : {})}
        icon={<ForkIcon />}
        label={label}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="strategy-lineage-chip"
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 380 } } }}
        data-testid="strategy-lineage-popover"
      >
        {/* Fix round 1 (M4): the failed state KEEPS the review-only semantics — one
            line carrying both, so nothing is traded away for the explanation. */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {generationFailed
            ? 'Fork lineage · review only. The fork’s generation failed before any block was saved — the lineage is kept; generate again from this strategy or delete it.'
            : 'Fork lineage · review only'}
        </Typography>
        {manifest.predecessorVersion && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Immutable source version: {shortId(manifest.predecessorVersion)}
          </Typography>
        )}
        {manifest.forkHypothesis && (
          <Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 1.5, py: 0.5, mb: 1.5 }}>
            <Typography variant="body2" fontStyle="italic">“{manifest.forkHypothesis}”</Typography>
          </Box>
        )}
        {manifest.inputDiff?.insightsAnalysis && (
          <Typography variant="body2">
            Insights analysis: {shortId(manifest.inputDiff.insightsAnalysis.from)} → {shortId(manifest.inputDiff.insightsAnalysis.to)}
          </Typography>
        )}
        {manifest.inputDiff?.variableSetVersion && (
          <Typography variant="body2">
            Variable set: v{manifest.inputDiff.variableSetVersion.from} → v{manifest.inputDiff.variableSetVersion.to}
          </Typography>
        )}
        {/* Fix round 1 (M3): a durable `forkLineage` record carries no `inputDiff`, and
            «Tasks: unchanged» would be a claim nobody computed. */}
        {manifest.inputDiff && (
          <Typography variant="body2">
            Tasks: {manifest.inputDiff.taskSetChanged ? 'changed vs predecessor' : 'unchanged'}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
          This lineage does not start an experiment or publish the strategy automatically.
        </Typography>
      </Popover>
    </>
  );
};
